import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { Input, Form, Button, DatePicker, message, Row, Col, Modal } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { serverFetch } from "@extensions";
import "./account.css";
import { useGetIsPhone } from "extensions";
import { useCompany } from "@redux";

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

const hasEmailInProfile = (c) =>
  c?.email != null && String(c.email).trim() !== "";

const Account = () => {
  const company = useCompany();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState({});
  const isPhone = useGetIsPhone();

  const [verifyEmail, setVerifyEmail] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [authGuid, setAuthGuid] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const backgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;
  // const [formValid, setFormValid] = useState(false);

  const getData = useCallback(() => {
    setLoading(true);
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((data) => {
        const parsed = data.birthday ? dayjs(data.birthday) : null;
        const dateBirth = parsed && typeof parsed.isValid === "function" && parsed.isValid() ? parsed : null;
        const newClient = {
          ...data,
          birthday: dateBirth,
        };
        setClient(newClient);
        form.setFieldsValue({
          ...newClient,
          dateBirth: dateBirth,
        });
        if (hasEmailInProfile(newClient)) {
          setVerifyEmail("");
          setCodeSent(false);
          setAuthGuid(null);
          setVerifyCode("");
        }
        setLoading(false);
      })
      .catch(() => {
        message.error("Ошибка получения пользователя");
        setLoading(false);
      });
  }, [form]);

  useEffect(() => {
    getData();
  }, [getData]);

  const onFinish = () => {
    setLoading(true);
    serverFetch("OnlineClient/info", { method: "PUT", bodyData: client })
      .then(() => {
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(errMsg(e));
      });
  };

  const onAutopayCancel = () => {
    if (window.confirm("Отменить подписку?")) {
      setLoading(true);
      serverFetch("account/AutopayCancel", { method: "POST" })
        .then(getData)
        .catch((e) => {
          setLoading(false);
          message.error(`Ошибка ${e.UserMessage}`);
        });
    }
  };

  const onValuesChange = (value) => {
    setClient({
      ...client,
      ...value,
      ...(value.dateBirth !== undefined && { birthday: value.dateBirth }),
    });
  };

  const openQrModal = () => {
    if (isPhone && client.card) {
      setIsQrModalOpen(true);
    }
  };

  const onQrKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openQrModal();
    }
  };

  const handleVerifyEmailStart = () => {
    const email = String(verifyEmail || "").trim();
    if (!email || !email.includes("@")) {
      message.warning("Укажите корректный адрес электронной почты");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailStart", {
      method: "POST",
      bodyData: { email },
    })
      .then((data) => {
        const guid = data?.authCode ?? data?.authGuid;
        if (!guid) {
          message.error("Не получен код авторизации");
          return;
        }
        setAuthGuid(guid);
        setCodeSent(true);
        message.success("Код отправлен");
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  const handleVerifyEmailConfirm = () => {
    const code = String(verifyCode || "").trim();
    if (!code) {
      message.warning("Введите код из письма");
      return;
    }
    if (!authGuid) {
      message.error("Сначала запросите код");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailConfirm", {
      method: "POST",
      bodyData: { authGuid, code },
    })
      .then(() => {
        message.success("Почта подтверждена");
        window.location.reload();
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  const qrModal = isPhone && client.card ? (
    <Modal
      open={isQrModalOpen}
      onCancel={() => setIsQrModalOpen(false)}
      footer={null}
      centered
      width="calc(100vw - 24px)"
      wrapClassName="account-qr-modal"
      destroyOnClose
    >
      <div className="account-qr-modal-content">
        <QRCodeSVG value={client.card} size={320} level="H" />
      </div>
    </Modal>
  ) : null;

  const divCard = client.autopayCode ? 
    <>
      <Form.Item label="Автопродление"><Input size="middle" name="autopayCard" disabled value={client.autopayCard} /></Form.Item>
      <Form.Item label=" "><Button onClick={onAutopayCancel}>Отменить подписку</Button></Form.Item>
    </> : null
  ;

  const divPhone = isPhone ? <>
    <Form.Item>
      <Row wrapperCol={24}>
        <Col span={24}>
          <Form.Item label="Телефон">{client.phone}</Form.Item>
          <Form.Item label="Дата рождения" name="dateBirth"><DatePicker format="DD.MM.YYYY" /></Form.Item>
          <Form.Item label="Почта" name="email"><Input disabled /></Form.Item>
        </Col>
        <Col span={24}>
          {client.card && <div className="account-qr-code account-qr-code_clickable" role="button" tabIndex={0} onClick={openQrModal} onKeyDown={onQrKeyDown}>
            <QRCodeSVG value={client.card} size={150} level="H" />
          </div>}
        </Col>
      </Row>
    </Form.Item>
  </> : <>
    <Form.Item label="Телефон">{client.phone}</Form.Item>
    <Form.Item label="Дата рождения" name="dateBirth"><DatePicker format="DD.MM.YYYY" /></Form.Item>
    <Form.Item label="Почта" name="email"><Input disabled /></Form.Item>
  </>

  const formContent = (
    <>
      <Form
        colon={false}
        form={form}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17 }}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
        className="account-form account-form-root"
      >
        <div className="auth-title">Мои данные</div>
        <div className="account-content-wrapper">
          <div className="account-form-inner">
            <Form.Item label="ФИО" name="name">
              <Input disabled={loading} maxLength={100} />
            </Form.Item>
            {divPhone}
            {divCard}
            <Form.Item label={isPhone ? "" : " "}>
              <Button className="btn-blue-raund" size="middle" loading={loading} htmlType="submit">
                Сохранить
              </Button>
            </Form.Item>
            <label className="prompt">
              Нажимая на кнопку &quot;Сохранить&quot;, вы даете согласие на обработку{" "}
              <a href="/article/agreement" target="_blank" rel="noreferrer">
                персональных данных
              </a>{" "}
              и соглашаетесь с{" "}
              <a href="/article/rules" target="_blank" rel="noreferrer">
                Условиями и правилами
              </a>{" "}
              сервиса
            </label>
          </div>
          {client.card && !isPhone && (
            <div className="account-qr-code">
              <QRCodeSVG value={client.card} size={200} level="H" />
            </div>
          )}
        </div>
        {!hasEmailInProfile(client) && (
          <div className="account-content-wrapper account-email-verify-outer">
            <div className="account-form-inner account-email-verify-inner">
              <div className="auth-title account-email-verify-title">Подтверждение почты</div>
              <Form.Item label="Почта">
                <Input
                  type="email"
                  value={verifyEmail}
                  onChange={(e) => setVerifyEmail(e.target.value)}
                  disabled={codeSent}
                  placeholder="name@example.com"
                  maxLength={200}
                />
              </Form.Item>
              {!codeSent && (
                <Form.Item label=" ">
                  <Button
                    type="primary"
                    loading={verifyLoading}
                    onClick={handleVerifyEmailStart}
                  >
                    Получить код
                  </Button>
                </Form.Item>
              )}
              {codeSent && (
                <>
                  <Form.Item label=" " className="account-email-verify-hint-item">
                    <p className="account-email-verify-hint">
                      На указанную почту был отправлен код авторизации — введите его.
                    </p>
                  </Form.Item>
                  <Form.Item label="Код авторизации">
                    <Input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder="Код из письма"
                      maxLength={32}
                      autoComplete="one-time-code"
                    />
                  </Form.Item>
                  <Form.Item label=" ">
                    <Button
                      type="primary"
                      loading={verifyLoading}
                      onClick={handleVerifyEmailConfirm}
                    >
                      Подтвердить
                    </Button>
                  </Form.Item>
                </>
              )}
            </div>
            {client.card && !isPhone && (
              <div className="account-email-verify-qr-spacer" aria-hidden />
            )}
          </div>
        )}
      </Form>
      {qrModal}
    </>
  );

  return (<div className="auth-context" style={backgroundStyle}>{formContent}</div>);
};

export default Account;
